import typography from "./typography";
import { createTheme } from "@mui/material";
import { green } from "@mui/material/colors";

declare module "@mui/material" {
  interface Theme {
    status: {
      danger: string;
    };
    navBar: {
      height: string;
    };
    dashboardDrawer: {
      width: number;
    };
    notificationDrawer: {
      width: number;
    };
    messenger: {
      surveyMessageBg: string;
      broadcastMessageBg: string;
      leadTextingMessageBg: string;
      highlightedMessageBg: string;
    };
  }
  interface ThemeOptions {
    status?: {
      danger?: string;
    };
    navBar: {
      height: string;
    };
    dashboardDrawer: {
      width: number;
    };
    notificationDrawer: {
      width: number;
    };
    messenger: {
      surveyMessageBg: string;
      broadcastMessageBg: string;
      leadTextingMessageBg: string;
      highlightedMessageBg: string;
    };
  }
}

export const CustomTheme = (mode: "light" | "dark") =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: mode === "light" ? "#1976d2" : "#90caf9",
        light: mode === "light" ? "#42a5f5" : "#e3f2fd",
        dark: mode === "light" ? "#1565c0" : "#42a5f5",
        contrastText: mode === "light" ? "#ffffff" : "#000000",
      },
      secondary: {
        main: mode === "light" ? "#dc004e" : "#f48fb1",
        light: mode === "light" ? "#ff5983" : "#fce4ec",
        dark: mode === "light" ? "#9a0036" : "#c2185b",
        contrastText: mode === "light" ? "#ffffff" : "#000000",
      },
      error: {
        main: mode === "light" ? "#d32f2f" : "#f44336",
        light: mode === "light" ? "#ef5350" : "#e57373",
        dark: mode === "light" ? "#c62828" : "#d32f2f",
        contrastText: mode === "light" ? "#ffffff" : "#000000",
      },
      warning: {
        main: mode === "light" ? "#ed6c02" : "#ff9800",
        light: mode === "light" ? "#ff9800" : "#ffb74d",
        dark: mode === "light" ? "#e65100" : "#f57c00",
        contrastText: mode === "light" ? "#ffffff" : "#000000",
      },
      info: {
        main: mode === "light" ? "#0288d1" : "#29b6f6",
        light: mode === "light" ? "#03a9f4" : "#4fc3f7",
        dark: mode === "light" ? "#01579b" : "#0288d1",
        contrastText: mode === "light" ? "#ffffff" : "#000000",
      },
      success: {
        main: mode === "light" ? "#2e7d32" : "#66bb6a",
        light: mode === "light" ? "#4caf50" : "#81c784",
        dark: mode === "light" ? "#1b5e20" : "#388e3c",
        contrastText: mode === "light" ? "#ffffff" : "#000000",
      },
      text: {
        primary: mode === "light" ? "rgba(0, 0, 0, 0.87)" : "#ffffff",
        secondary:
          mode === "light" ? "rgba(0, 0, 0, 0.6)" : "rgba(255, 255, 255, 0.7)",
        disabled:
          mode === "light" ? "rgba(0, 0, 0, 0.38)" : "rgba(255, 255, 255, 0.5)",
      },
      background: {
        default: mode === "light" ? "#F9F9FA" : "#121212",
        paper: mode === "light" ? "#ffffff" : "#1e1e1e",
      },
      divider:
        mode === "light" ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)",
    },
    typography,
    zIndex: {
      appBar: 1200,
      drawer: 1100,
    },
    status: {
      danger: "red",
    },
    navBar: {
      height: "48px",
    },
    dashboardDrawer: {
      width: 200,
    },
    notificationDrawer: {
      width: 0,
    },
    components: {
      MuiTab: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&.Mui-selected": {
              color: "green",
              borderBottom: "2px solid orange",
            },
          }),
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: "#2371E7", // Default indicator color
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            "& ::-webkit-scrollbar": {
              width: "20px",
            },
            "& ::-webkit-scrollbar-track": {
              backgroundColor: mode === "light" ? "#f1f1f1" : "#2d2d2d",
            },
            "& ::-webkit-scrollbar-thumb": {
              backgroundColor: mode === "light" ? "#c1c1c1" : "#555555",
              borderRadius: "10px",
            },
            "& ::-webkit-scrollbar-thumb:hover": {
              backgroundColor: mode === "light" ? "#a8a8a8" : "#777777",
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
              backgroundColor: green[500],
            },
          },
          switchBase: {
            "&.Mui-checked": {
              color: green[500],
              "& + .MuiSwitch-track": {
                backgroundColor: green[500],
              },
            },
          },
          colorPrimary: {
            "&.Mui-checked": {
              color: green[500],
              "& + .MuiSwitch-track": {
                backgroundColor: green[500],
              },
            },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: mode === "light" ? "#181F2A" : "#1a1a1a",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            "&.Mui-selected": {
              backgroundColor:
                theme.palette.mode === "light"
                  ? theme.palette.primary.main
                  : theme.palette.primary.dark,
              color: theme.palette.primary.contrastText,
            },
          }),
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.secondary,
            "&.Mui-selected &": {
              color: theme.palette.primary.contrastText,
            },
          }),
        },
      },
    },
    messenger: {
      surveyMessageBg: "#42a9bf",
      broadcastMessageBg: "#5742bf",
      leadTextingMessageBg: "#67AE3F",
      highlightedMessageBg: "#3f51b5c4",
    },
  });

export default CustomTheme;
